import { useEffect, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch, uiActions, locationActions } from '../store/index'
import { useGeolocation } from './useGeolocation'
import { locationService } from '../services/locationService'
import { haversine, metersToMiles } from '../utils/haversine'

// Polling intervals in milliseconds
const INTERVALS = {
  idle: 300000, // 5 minutes
  active: 15000, // 15 seconds
  nearHandoff: 5000, // 5 seconds
}

const PACE_WINDOW_MILES = 0.25
const MAX_HISTORY = 200

interface GpsPoint {
  lat: number
  lng: number
  timestamp: number
}

export function useLocationPolling(runnerId: number | null, enabled = true, highAccuracy = true) {
  const dispatch = useAppDispatch()
  const raceStatus = useAppSelector(state => state.race.status)
  const isNearHandoff = useAppSelector(state => state.ui.isNearHandoff)
  const { location, startTracking, stopTracking } = useGeolocation(highAccuracy)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastLocationRef = useRef<{ lat: number; lng: number } | null>(null)
  const lastPostTimeRef = useRef<number>(0)
  const latestLocationRef = useRef<typeof location | null>(null)
  const gpsHistoryRef = useRef<GpsPoint[]>([])
  const [realtimePace, setRealtimePace] = useState<number | null>(null)

  // Determine polling interval based on race status
  const getInterval = () => {
    if (raceStatus !== 'in_progress') return INTERVALS.idle
    if (isNearHandoff) return INTERVALS.nearHandoff
    return INTERVALS.active
  }

  // Dispatch to Redux on every GPS update — gives the runner's own device real-time position
  // Also maintain GPS history buffer for rolling pace calculation
  useEffect(() => {
    if (!location || !runnerId) return

    // Always keep the ref current so the polling interval can read the latest fix
    latestLocationRef.current = location

    // Push to Redux immediately (real-time map update on runner's own device)
    dispatch(locationActions.updateLocation({ runnerId, location }))

    // Append to GPS history buffer
    const point: GpsPoint = {
      lat: location.latitude,
      lng: location.longitude,
      timestamp: new Date(location.timestamp).getTime(),
    }
    const history = [...gpsHistoryRef.current, point].slice(-MAX_HISTORY)
    gpsHistoryRef.current = history

    // Calculate rolling pace from last PACE_WINDOW_MILES
    if (history.length < 2) return

    const newest = history[history.length - 1]
    let cumulativeMiles = 0
    let windowStartIdx = 0

    for (let i = history.length - 2; i >= 0; i--) {
      const segDist = metersToMiles(
        haversine(history[i].lat, history[i].lng, history[i + 1].lat, history[i + 1].lng)
      )
      cumulativeMiles += segDist
      if (cumulativeMiles >= PACE_WINDOW_MILES) {
        windowStartIdx = i
        break
      }
    }

    if (cumulativeMiles < 0.05) return // Not enough distance yet

    const windowStart = history[windowStartIdx]
    const elapsedMinutes = (newest.timestamp - windowStart.timestamp) / 60000
    if (elapsedMinutes <= 0) return

    const pace = elapsedMinutes / cumulativeMiles
    // Sanity check: between 5 and 30 min/mile
    if (pace >= 5 && pace <= 30) {
      setRealtimePace(Math.round(pace * 100) / 100)
    }
  }, [location, runnerId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopTracking()
        if (intervalRef.current) clearInterval(intervalRef.current)
      } else {
        // Clear GPS history — pace calculated across a background gap would be wrong
        gpsHistoryRef.current = []
        startTracking()
        setupPolling()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Setup polling interval — sends location to server at throttled rate
  const setupPolling = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)

    if (!enabled || !runnerId || document.hidden) return

    startTracking()

    intervalRef.current = setInterval(async () => {
      const loc = latestLocationRef.current
      if (!loc || !runnerId) return

      // Only send if moved more than 5 meters, unless 2 minutes have passed (keepalive)
      const now = Date.now()
      if (lastLocationRef.current && now - lastPostTimeRef.current < 120000) {
        const dx = loc.latitude - lastLocationRef.current.lat
        const dy = loc.longitude - lastLocationRef.current.lng
        const distance = Math.sqrt(dx * dx + dy * dy) * 111000 // rough conversion to meters
        if (distance < 5) return
      }

      try {
        await locationService.postLocation({
          runnerId,
          latitude: loc.latitude,
          longitude: loc.longitude,
          accuracy: loc.accuracy || 0,
          timestamp: loc.timestamp,
        })

        lastLocationRef.current = { lat: loc.latitude, lng: loc.longitude }
        lastPostTimeRef.current = Date.now()

        dispatch(uiActions.setPollingInterval(getInterval() as any))
      } catch (error) {
        console.error('Failed to post location:', error)
      }
    }, getInterval())
  }

  // Adjust interval when race status or handoff status changes
  useEffect(() => {
    setupPolling()
  }, [raceStatus, isNearHandoff, enabled, runnerId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  return {
    location,
    interval: getInterval(),
    realtimePace,
  }
}
